import { useState, useEffect } from 'react';

/**
 * Custom hook for responsive design that detects if a media query matches
 * 
 * @param {string} query - CSS media query string (e.g. '(min-width: 768px)')
 * @returns {boolean} - Whether the media query matches
 * 
 * @example
 * const isDesktop = useMediaQuery('(min-width: 768px)');
 * if (isDesktop) {
 *   // Render desktop view
 * } else {
 *   // Render mobile view
 * }
 */
function useMediaQuery(query: string): boolean {
  // Initialize with the current match state if window is available (client-side)
  // Otherwise default to false for SSR
  const getMatches = (): boolean => {
    // Check if window is defined (to avoid SSR issues)
    if (typeof window !== 'undefined') {
      return window.matchMedia(query).matches;
    }
    return false;
  };

  const [matches, setMatches] = useState<boolean>(getMatches);

  useEffect(() => {
    // Avoid running on the server
    if (typeof window === 'undefined') {
      return undefined;
    }

    const mediaQuery = window.matchMedia(query);
    
    // Update the state initially and on changes
    const handleChange = (): void => {
      setMatches(mediaQuery.matches);
    };

    // Set up event listener for changes
    if (mediaQuery.addEventListener) {
      // Modern browsers
      mediaQuery.addEventListener('change', handleChange);
    } else {
      // Older browsers (IE, older Safari/Chrome)
      mediaQuery.addListener(handleChange);
    }

    // Call once initially to set the initial state
    handleChange();

    // Clean up
    return () => {
      if (mediaQuery.removeEventListener) {
        mediaQuery.removeEventListener('change', handleChange);
      } else {
        mediaQuery.removeListener(handleChange);
      }
    };
  }, [query]);

  return matches;
}

export { useMediaQuery }; 